import {
  adminGymCreatedSchema,
  adminGymListItemSchema,
  gymTimeZoneOptionSchema,
  type AdminGymCreate,
  type AdminGymCreated,
  type AdminGymListItem,
  type GymTimeZoneOption,
} from '@steado/admin-contract/gyms';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export interface GymsFilters {
  readonly search?: string | undefined;
  readonly setup?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface GymsPage {
  readonly items: AdminGymListItem[];
  readonly pagination: PaginationMeta;
}

const gymsResponseSchema = z.object({
  items: z.array(adminGymListItemSchema),
  pagination: paginationMetaSchema,
});

export const GYMS_PAGE_SIZE = 20;

export function gymsQueryOptions(api: ApiClient, filters: GymsFilters) {
  // Key by key: a filter left out of this literal would never reach the API.
  const query: Record<string, QueryValue> = {
    search: filters.search,
    setup: filters.setup,
    cursor: filters.cursor,
    limit: GYMS_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'gyms', filters],
    queryFn: async (): Promise<GymsPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/gyms',
        query,
        schema: z.array(adminGymListItemSchema),
      });
      return gymsResponseSchema.parse({ items: response.data, pagination: response.meta.pagination });
    },
    // The previous page stays on screen while the next one loads.
    placeholderData: (previous) => previous,
  });
}

/** Creates a gym and its owner; the API emails the owner an invite. */
export function createGym(api: ApiClient, body: AdminGymCreate): Promise<AdminGymCreated> {
  return api.request({ path: '/admin/gyms', method: 'POST', body, schema: adminGymCreatedSchema });
}

/**
 * The zones a gym may be in, from the API that owns the list (`GET /time-zones`,
 * public, the same list the owner app offers). It changes with a deploy, not
 * within a session.
 */
export function gymTimeZonesQueryOptions(api: ApiClient) {
  return queryOptions({
    queryKey: ['gym-time-zones'],
    queryFn: (): Promise<GymTimeZoneOption[]> =>
      api.request({ path: '/time-zones', schema: z.array(gymTimeZoneOptionSchema) }),
    staleTime: Infinity,
  });
}
